/**
 * Filtro de data (dia único) para o monitoramento externo.
 */

import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { IconCalendar } from '@tabler/icons-react';
import { EcClickUpFilterChip } from '../../experiencia-cliente/components/EcClickUpFilterChip';
import {
  RangeCalendarSingle,
  getCalendarBounds,
  toYmd,
} from '../../equipment-consult/components/EquipmentConsultProductionPeriodPicker';
import { ymdToBr } from '../../relatorios/utils/relatoriosPeriodDateUtils';
import '../../equipment-consult/components/EquipmentConsultProductionPeriodPicker.css';
import './MonitoramentoPage.css';

export function getTodayYmd() {
  return toYmd(new Date());
}

const POPOVER_WIDTH = 272;

export function DayPeriodModal({ anchorRef, anchorRect, selectedDate, onApply, onClose }) {
  const modalRef = useRef(null);
  const [draftDate, setDraftDate] = useState(selectedDate || getTodayYmd());
  const bounds = getCalendarBounds();

  const resolvePosition = useCallback((rect) => {
    const popoverWidth = Math.min(POPOVER_WIDTH, window.innerWidth - 24);
    if (!rect) return null;
    let left = rect.left;
    if (left + popoverWidth > window.innerWidth - 12) {
      left = Math.max(12, window.innerWidth - popoverWidth - 12);
    }
    const spaceBelow = window.innerHeight - rect.bottom - 12;
    if (spaceBelow >= 280) {
      return { top: rect.bottom + 8, left, bottom: 'auto', right: 'auto' };
    }
    return {
      bottom: window.innerHeight - rect.top + 8,
      left: Math.max(12, rect.left),
      top: 'auto',
      right: 'auto',
    };
  }, []);

  const [position, setPosition] = useState(() =>
    anchorRect ? resolvePosition(anchorRect) : null
  );

  useEffect(() => {
    setDraftDate(selectedDate || getTodayYmd());
  }, [selectedDate]);

  useLayoutEffect(() => {
    if (anchorRect) {
      setPosition(resolvePosition(anchorRect));
      return;
    }
    const el = anchorRef?.current;
    if (!el) return;
    setPosition(resolvePosition(el.getBoundingClientRect()));
  }, [anchorRef, anchorRect, resolvePosition]);

  useEffect(() => {
    const onDoc = (e) => {
      if (modalRef.current?.contains(e.target)) return;
      if (anchorRef?.current?.contains(e.target)) return;
      if (e.target.closest?.('.ec-period-nav')) return;
      onClose();
    };
    document.addEventListener('mousedown', onDoc, true);
    return () => document.removeEventListener('mousedown', onDoc, true);
  }, [anchorRef, onClose]);

  if (!position) return null;

  const style = {
    position: 'fixed',
    zIndex: 10010,
    width: Math.min(POPOVER_WIDTH, window.innerWidth - 24),
    ...(position.top != null && position.top !== 'auto' ? { top: `${position.top}px` } : { top: 'auto' }),
    ...(position.bottom != null && position.bottom !== 'auto'
      ? { bottom: `${position.bottom}px` }
      : { bottom: 'auto' }),
    ...(position.left != null && position.left !== 'auto'
      ? { left: `${position.left}px` }
      : { left: 'auto' }),
    ...(position.right != null && position.right !== 'auto'
      ? { right: `${position.right}px` }
      : { right: 'auto' }),
  };

  return ReactDOM.createPortal(
    <div
      ref={modalRef}
      className="equipment-consult-period-modal equipment-consult-period-modal--no-head equipment-consult-period-modal--no-actions-border ec-module-popover monitoramento-day-period-modal"
      style={style}
      onClick={(e) => e.stopPropagation()}
      role="dialog"
      aria-modal="true"
      aria-label="Filtrar por data"
    >
      <div className="ec-period-picker-body">
        <RangeCalendarSingle
          minDate={bounds.minDate}
          maxDate={bounds.maxDate}
          startDate={draftDate}
          endDate={draftDate}
          dayMode
          onDateSelect={(dateStr) => {
            if (dateStr) setDraftDate(dateStr);
          }}
        />
      </div>
      <div className="ec-period-picker-actions">
        <button
          type="button"
          className="user-form-link-btn user-form-footer-btn"
          onClick={() => {
            const today = getTodayYmd();
            setDraftDate(today);
            onApply(today);
            onClose();
          }}
        >
          Hoje
        </button>
        <button
          type="button"
          className="user-form-profile-save-btn user-form-footer-btn"
          disabled={!draftDate}
          onClick={() => {
            if (!draftDate) return;
            onApply(draftDate);
            onClose();
          }}
        >
          Aplicar
        </button>
      </div>
    </div>,
    document.body
  );
}

export function MonitoramentoDayFilter({ selectedDate, onApply, disabled = false }) {
  const btnRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [anchorRect, setAnchorRect] = useState(null);

  const valueLabel = selectedDate ? ymdToBr(selectedDate) : ymdToBr(getTodayYmd());

  const handleToggle = useCallback(() => {
    if (disabled) return;
    const rect = btnRef.current?.getBoundingClientRect();
    setAnchorRect(rect || null);
    setOpen((v) => !v);
  }, [disabled]);

  return (
    <>
      <EcClickUpFilterChip
        anchorRef={btnRef}
        label="Data"
        value={valueLabel}
        icon={IconCalendar}
        hasSelection={Boolean(selectedDate)}
        isOpen={open}
        disabled={disabled}
        ariaLabel="Filtrar por data"
        onClick={handleToggle}
      />
      {open && (
        <DayPeriodModal
          anchorRef={btnRef}
          anchorRect={anchorRect}
          selectedDate={selectedDate}
          onApply={onApply}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}
